import type { TFunction } from 'i18next'
import type { SaasApiError } from '../../../api'

/** Router structured error codes → Canvas product copy (never NETWORK_ERROR for business states). */

export const VIDEO_ERROR_ENGINE_OFFLINE = 'ENGINE_OFFLINE'
export const VIDEO_ERROR_ENGINE_STARTING = 'ENGINE_STARTING'
export const VIDEO_ERROR_WORKER_UNAVAILABLE = 'WORKER_UNAVAILABLE'
export const VIDEO_ERROR_ENTITLEMENT_DENIED = 'ENTITLEMENT_DENIED'
export const VIDEO_ERROR_CLOUD_UNAVAILABLE = 'CLOUD_PROVIDER_UNAVAILABLE'

const VIDEO_ERROR_CODES = [
  'WORKER_UNAVAILABLE',
  // VistoeraTASK-00210 — the genuine "engine binaries/model weights not
  // installed" gate (backend `video_generation_contract.ENGINE_NOT_INSTALLED`).
  // Distinct from ENGINE_OFFLINE/ENGINE_STARTING, which describe an
  // installed-but-not-currently-resident engine that IS still activatable —
  // do not merge this with those codes.
  'ENGINE_NOT_INSTALLED',
  'ENGINE_OFFLINE',
  'ENGINE_STARTING',
  'ENGINE_START_TIMEOUT',
  'ENGINE_START_FAILED',
  'ENGINE_RUNTIME_CRASHED',
  'ENGINE_ERROR',
  'ENTITLEMENT_DENIED',
  'CLOUD_PROVIDER_UNAVAILABLE',
  'CLOUD_FALLBACK_NOT_ALLOWED',
  'CAPABILITY_UNAVAILABLE',
  'MODEL_NOT_ENABLED',
  'VALIDATION_ERROR',
  'GENERATION_FAILED',
  // VISTOERA_FUNCTION_NODE_PROVIDER_SINGLE_SOURCE_OF_TRUTH_AND_CANVAS_
  // GENERATE_VIDEO_PROVIDER_DRIFT_FORENSIC_P0_V1 §P9 — Submission Gate
  // domain errors (backend `video_generation_contract.py`).
  'PROVIDER_NOT_ENABLED_FOR_FUNCTION_NODE',
  'MODEL_NOT_BOUND_TO_FUNCTION_NODE',
  'PROVIDER_EXECUTION_NOT_SUPPORTED',
  // VISTOERA_GENERATE_VIDEO_CANONICAL_PROVIDER_ID_AND_ADMIN_DEFAULT_
  // PROVIDER_END_TO_END_P0_V1 §P6/§P7 — canonical Provider/Channel
  // consistency gate (backend `video_generation_contract.
  // PROVIDER_CHANNEL_MISMATCH`).
  'PROVIDER_CHANNEL_MISMATCH',
  'INPUT_ASSET_REJECTED',
] as const

export function localizeVideoGenerationSubmitError(err: SaasApiError, t: TFunction<'canvas'>): string {
  if (err.code && VIDEO_ERROR_CODES.includes(err.code as (typeof VIDEO_ERROR_CODES)[number])) {
    return t(`videoErrors.${err.code}`)
  }
  const message = (err.message || '').trim()
  if (!message) return t('generation.errorSubmitFailed')
  if (message === t('videoErrors.networkFailure')) return message
  if (message === t('videoErrors.networkTimeout')) return message
  return message
}

export function isVideoEngineStartingError(code: string | undefined): boolean {
  return code === VIDEO_ERROR_ENGINE_STARTING
}

export function isTrueNetworkError(err: SaasApiError, t: TFunction<'canvas'>): boolean {
  if (err.code === 'NETWORK_ERROR') return true
  const message = (err.message || '').trim()
  return message === t('videoErrors.networkFailure') || message === t('videoErrors.networkTimeout')
}
